import { ChevronDown, ChevronLeft, ChevronRight, ExternalLink, PlayCircle } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import { FaGoogle } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { googleAdsFetch } from 'sprites-types/workflow/tools/googleAdsFetch'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

/** Ensure a URL string has a protocol so it works as an href. */
function normalizeUrl(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

/** Collect all image URLs from a variation object, de-duplicated and type-safe. */
function getVariationImages(variation: unknown): string[] {
  if (!variation || typeof variation !== 'object') return []
  const v = variation as Record<string, unknown>
  const candidates: unknown[] = [
    v['image'],
    v['imageUrl'],
    ...(Array.isArray(v['images']) ? v['images'] : []),
  ]
  const seen = new Set<string>()
  const result: string[] = []
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0 && !seen.has(c)) {
      seen.add(c)
      result.push(c)
    }
  }
  return result
}

/** Extract a YouTube video ID from a watch URL, short URL, or embed URL. */
function extractYoutubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  )
  return match?.[1] ?? null
}

function GoogleAdItem({ ad, className }: { ad: any; className?: string }) {
  const format = ad.format || 'unknown'
  const variations = ad.variations || []
  const overallImpressions = ad.overallImpressions || {}
  const impressionsMin = overallImpressions.min
  const impressionsMax = overallImpressions.max
  const [activeVariation, setActiveVariation] = useState(0)

  const formatImpressions = () => {
    if (impressionsMin && impressionsMax) {
      return `${impressionsMin} - ${impressionsMax}`
    }
    if (impressionsMin) return `${impressionsMin}+`
    if (impressionsMax) return `Up to ${impressionsMax}`
    return null
  }

  const getFormatColor = () => {
    switch (format.toLowerCase()) {
      case 'image':
        return 'bg-blue-100 text-blue-700'
      case 'video':
        return 'bg-red-100 text-red-700'
      case 'text':
        return 'bg-green-100 text-green-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const variation = variations[activeVariation] ?? null

  const imageSrcs = getVariationImages(variation)

  // Resolve YouTube video ID from youtubeUrl or raw videoId field
  const youtubeId =
    (variation?.youtubeUrl ? extractYoutubeId(variation.youtubeUrl) : null) ??
    variation?.videoId ??
    null

  return (
    <div
      className={`overflow-hidden rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      {/* Creative content — always render when a variation exists */}
      {variation && (
        <div className="px-4 pt-4">
          {/* Images — render all collected sources */}
          {imageSrcs.map((src, i) => (
            <Image
              key={i}
              src={src}
              alt={`Ad variation ${activeVariation + 1}${imageSrcs.length > 1 ? ` image ${i + 1}` : ''}`}
              width={800}
              height={300}
              className="mb-2 w-full rounded-xl border border-gray-100 bg-gray-50 object-contain last:mb-3"
              style={{ maxHeight: '300px' }}
              unoptimized
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          ))}

          {/* Video embed */}
          {youtubeId && (
            <div className="mb-3 overflow-hidden rounded-xl border border-gray-100">
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}`}
                title={`Ad video variation ${activeVariation + 1}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="aspect-video w-full"
              />
            </div>
          )}

          {/* Video link fallback when no extractable ID */}
          {!youtubeId && variation.youtubeUrl && (
            <a
              href={variation.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-3 inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100"
            >
              <PlayCircle className="h-3.5 w-3.5" />
              Watch on YouTube
            </a>
          )}

          {/* Headline */}
          {variation.headline && (
            <div className="mb-1 text-sm font-semibold text-gray-900">{variation.headline}</div>
          )}

          {/* Description */}
          {variation.description && (
            <div className="mb-2 text-xs leading-relaxed text-gray-600">
              {variation.description}
            </div>
          )}

          {/* allText fallback */}
          {!variation.headline && !variation.description && variation.allText && (
            <div className="mb-2 text-xs leading-relaxed text-gray-600">{variation.allText}</div>
          )}

          {/* Destination URL */}
          {(variation.destinationUrl || variation.visibleUrl) && (
            <a
              href={normalizeUrl(variation.destinationUrl || variation.visibleUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-3 w-3" />
              <span className="truncate">
                {(variation.visibleUrl || variation.destinationUrl || '').substring(0, 50)}
              </span>
            </a>
          )}
        </div>
      )}

      {/* Identifiers */}
      {(ad.advertiserId || ad.creativeId) && (
        <div className="mt-3 px-4 font-mono text-[10px] text-gray-400">
          {ad.advertiserId && <span>Advertiser {ad.advertiserId}</span>}
          {ad.advertiserId && ad.creativeId && <span className="mx-1">·</span>}
          {ad.creativeId && <span>Creative {ad.creativeId}</span>}
        </div>
      )}

      {/* Footer bar: stats + carousel nav + library link */}
      <div className="mt-2 flex items-center gap-2 border-t border-gray-100 px-4 py-2">
        {/* Format badge */}
        <div className={`rounded-full px-2 py-0.5 text-xs ${getFormatColor()}`}>
          {format.charAt(0).toUpperCase() + format.slice(1).toLowerCase()}
        </div>

        {/* CTA chip */}
        {variation?.callToAction && (
          <div className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
            {variation.callToAction.replace(/_/g, ' ')}
          </div>
        )}

        {/* Impressions */}
        {formatImpressions() && (
          <div className="text-xs text-gray-400">{formatImpressions()} impr.</div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Carousel nav (only when multiple variations) */}
        {variations.length > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() =>
                setActiveVariation((i) => (i - 1 + variations.length) % variations.length)
              }
              className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Previous variation"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span className="min-w-[2.5rem] text-center text-xs text-gray-400">
              {activeVariation + 1}/{variations.length}
            </span>
            <button
              onClick={() => setActiveVariation((i) => (i + 1) % variations.length)}
              className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Next variation"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Ad library link */}
        {ad.advertiserId && ad.creativeId && (
          <a
            href={`https://adstransparency.google.com/advertiser/${ad.advertiserId}/creative/${ad.creativeId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-blue-600"
            title="View in Google Ad Library"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  )
}

function GoogleAdsFetchDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsFetch, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const ads = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.ads ||
      (result as any)?.campaigns ||
      (result ? [result] : [])

  // Count ad formats
  const formatCounts = ads.reduce((counts: Record<string, number>, ad: any) => {
    const format = ad.format || 'unknown'
    counts[format] = (counts[format] || 0) + 1
    return counts
  }, {})

  const formatSummary = Object.entries(formatCounts)
    .map(([format, count]) => `${count} ${format}`)
    .join(' • ')

  if (ads.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <FaGoogle className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No Google Ads found</div>
        </div>
      </div>
    )
  }

  if (ads.length === 1) {
    return <GoogleAdItem ad={ads[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <FaGoogle className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                Google Ad Creatives
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {ads.length} {ads.length === 1 ? 'ad' : 'ads'} • {formatSummary}
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {ads.map((ad: any, index: number) => (
            <GoogleAdItem key={ad.creativeId || index} ad={ad} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadGoogleAdsFetch(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsFetchToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsFetch, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const ads = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.ads ||
      (result as any)?.campaigns ||
      (result ? [result] : [])

  if (ads.length === 0) {
    return `## Google Ads Data

No ads found.

---
`
  }

  const adsMarkdown = ads
    .map((ad: any) => {
      const format = ad.format || 'unknown'
      const variations = ad.variations || []
      const regions = ad.creativeRegions || []
      const lastShown = ad.lastShown ? new Date(ad.lastShown).toLocaleDateString() : null
      const firstShown = ad.firstShown ? new Date(ad.firstShown).toLocaleDateString() : null

      const overallImpressions = ad.overallImpressions || {}
      const impressionsMin = overallImpressions.min
      const impressionsMax = overallImpressions.max

      let impressionsText = null
      if (impressionsMin && impressionsMax) {
        impressionsText = `${impressionsMin} - ${impressionsMax}`
      } else if (impressionsMin) {
        impressionsText = `${impressionsMin}+`
      } else if (impressionsMax) {
        impressionsText = `Up to ${impressionsMax}`
      }

      let markdown = `### ${format.charAt(0).toUpperCase() + format.slice(1)} Ad\n\n`
      markdown += `**Creative ID:** ${ad.creativeId}\n`
      if (lastShown) markdown += `**Last Shown:** ${lastShown}\n`
      if (firstShown) markdown += `**First Shown:** ${firstShown}\n`
      if (impressionsText) markdown += `**Impressions:** ${impressionsText}\n`

      if (regions.length > 0) {
        markdown += `**Regions:** ${regions.map((r: any) => r.regionName || r.regionCode).join(', ')}\n`
      }

      if (variations.length > 0) {
        markdown += `\n**Variations (${variations.length}):**\n\n`
        variations.forEach((v: any, index: number) => {
          markdown += `${index + 1}. `
          if (v.headline) markdown += `**${v.headline}**\n   `
          if (v.description) markdown += `${v.description}\n   `
          if (v.allText) markdown += `${v.allText}\n   `
          if (v.callToAction) markdown += `CTA: ${v.callToAction.replace(/_/g, ' ')}\n   `
          if (v.youtubeUrl) markdown += `Video: ${v.youtubeUrl}\n   `
          if (v.destinationUrl || v.visibleUrl)
            markdown += `URL: ${normalizeUrl(v.destinationUrl || v.visibleUrl)}\n   `
          markdown += '\n'
        })
      }

      return markdown
    })
    .join('\n---\n\n')

  return `## Google Ad Creatives (${ads.length} ads)

${adsMarkdown}

---
`
}

export const GoogleAdsFetchDisplay = createWorkflowToolComponent(
  GoogleAdsFetchDisplayComponent,
  downloadGoogleAdsFetch,
  googleAdsFetchToMarkdown
)
